import React, { useCallback, useContext, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useClientUser } from '@/hooks';
import Table, { TableLoader } from '@/components/Table';
import { Context } from '../store';
import { QUERY_MANIFEST_ACTIVITY_LOG } from './graphql';
import { useColumns } from './columns';

const ManifestActivityLog = () => {
    const { state, callbacks } = useContext(Context);
    const { user_id, isOnwardAdmin, isImposter } = useClientUser();
    const manifest = state.manifest || {};
    const manifest_id = manifest.manifest_id;

    // Same visibility model as the orders ActivityLog.
    const condition = useMemo(() => {
        const filters = [{ manifest_id: { _eq: manifest_id } }];
        if (!isOnwardAdmin && !isImposter) {
            filters.push(
                { private_admin: { _eq: false } },
                { _or: [{ private_client_id: { _eq: user_id } }, { private_client_id: { _is_null: true } }] }
            );
        }
        return { _and: filters };
    }, [manifest_id, user_id, isOnwardAdmin, isImposter]);

    const { data, loading } = useQuery(QUERY_MANIFEST_ACTIVITY_LOG, {
        variables: { condition },
        skip: !manifest_id,
        fetchPolicy: 'cache-and-network',
    });

    const logs = useMemo(() => data?.order_activity_logs || [], [data]);

    // exception_id -> { exception, order, item } so an EXCEPTION row can open the resolve dialog.
    const exceptionIndex = useMemo(() => {
        const map = {};
        (manifest.orders || []).forEach((order) => {
            (order.manifestItems || []).forEach((item) => {
                (item?.exceptions || []).forEach((mapping) => {
                    const exception = mapping?.exception;
                    if (!exception?.exception_id || map[exception.exception_id]) return;
                    map[exception.exception_id] = { exception, order, item };
                });
            });
        });
        return map;
    }, [manifest.orders]);

    // Mirror the grid's resolveException, then open the already-mounted resolve modal.
    const onExceptionAction = useCallback(
        (found, readOnly) => {
            const { exception, order, item } = found;
            const siblings = (order?.itemsByOrderId || []).filter(
                (i) => i?.item_exception?.exception_id === exception.exception_id
            );
            callbacks.setResolveExceptionParams({
                exception,
                items: siblings.length ? siblings : [item],
                order,
                opt: readOnly ? { readOnly: true } : {},
            });
        },
        [callbacks]
    );

    const columns = useColumns({ exceptionIndex, onExceptionAction, editable: state.editable });

    if (loading && !logs.length) {
        return <TableLoader />;
    }

    return (
        <Table
            data={logs}
            columns={columns}
            callbacks={{ getRowId: (log) => log.log_id }}
            options={{ select: false, sortAsync: false }}
        />
    );
};

export default ManifestActivityLog;
